import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  Chip
} from '@mui/material';
import TableActions from 'components/TableActions';
import { formatCurrency, formatDate } from 'utils/formatters';

/**
 * Reusable DataTable component with built-in pagination, sorting, and actions
 *
 * @example
 * <DataTable
 *   columns={[
 *     { id: 'name', label: 'Name', accessor: 'name' },
 *     { id: 'email', label: 'Email', accessor: 'email' },
 *     { id: 'status', label: 'Status', accessor: 'status', type: 'status' }
 *   ]}
 *   data={users}
 *   loading={loading}
 *   pagination={{
 *     enabled: true,
 *     serverSide: true,
 *     page: 0,
 *     rowsPerPage: 15,
 *     total: 100,
 *     onPageChange: handlePageChange,
 *     onRowsPerPageChange: handleRowsPerPageChange
 *   }}
 *   actions={{
 *     onView: (row) => navigate(`/users/${row.id}`),
 *     onEdit: (row) => handleEdit(row),
 *     onDelete: (row) => handleDelete(row)
 *   }}
 * />
 */
const DataTable = ({
  columns,
  data = [],
  loading = false,
  pagination,
  actions,
  emptyMessage = 'No data found',
  onRowClick,
  rowKey = 'id',
  size = 'medium'
}) => {
  // Local pagination state (for client-side pagination)
  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(15);

  // Determine if using server-side or client-side pagination
  const isServerSide = pagination?.serverSide ?? false;
  const paginationEnabled = pagination?.enabled ?? true;

  // Get pagination values
  const currentPage = isServerSide ? (pagination?.page ?? 0) : localPage;
  const currentRowsPerPage = isServerSide ? (pagination?.rowsPerPage ?? 15) : localRowsPerPage;
  const totalRows = isServerSide ? (pagination?.total ?? data.length) : data.length;

  // Handle pagination changes
  const handlePageChange = (event, newPage) => {
    if (isServerSide && pagination?.onPageChange) {
      pagination.onPageChange(event, newPage);
    } else {
      setLocalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (isServerSide && pagination?.onRowsPerPageChange) {
      pagination.onRowsPerPageChange(event);
    } else {
      setLocalRowsPerPage(newRowsPerPage);
      setLocalPage(0);
    }
  };

  // Apply client-side pagination
  const paginatedData =
    !isServerSide && paginationEnabled ? data.slice(currentPage * currentRowsPerPage, (currentPage + 1) * currentRowsPerPage) : data;

  // Render cell content based on column type
  const renderCell = (column, row) => {
    const value = column.accessor ? row[column.accessor] : null;

    // Custom render function
    if (column.render) {
      return column.render(value, row);
    }

    // Type-based rendering
    switch (column.type) {
      case 'currency':
        return (
          <Typography variant="body2" align="right">
            {formatCurrency(value, row[column.currencyField || 'currency'])}
          </Typography>
        );

      case 'date':
        return <Typography variant="body2">{value ? formatDate(value) : '-'}</Typography>;

      case 'status':
        const statusConfig = column.statusConfig?.[value] || {};
        return <Chip label={statusConfig.label || value?.toUpperCase() || '-'} color={statusConfig.color || 'default'} size="small" />;

      case 'badge':
        return <Chip label={value || 0} size="small" variant="outlined" />;

      case 'link':
        return (
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', color: 'primary.main' }}
            onClick={(e) => {
              e.stopPropagation();
              if (column.onClick) column.onClick(row);
            }}
          >
            {value}
          </Typography>
        );

      default:
        // Handle nested accessors (e.g., 'user.name')
        let displayValue = value;
        if (column.accessor?.includes('.')) {
          const parts = column.accessor.split('.');
          displayValue = parts.reduce((obj, key) => obj?.[key], row);
        }

        // Primary text styling
        if (column.primary) {
          return (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500
              }}
            >
              {displayValue || '-'}
            </Typography>
          );
        }

        // Secondary text styling
        if (column.secondary) {
          return (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              {displayValue || '-'}
            </Typography>
          );
        }

        // Default text
        return <Typography variant="body2">{displayValue || '-'}</Typography>;
    }
  };

  // Get table actions for a row
  const getRowActions = (row) => {
    if (!actions) return null;

    const actionProps = {};
    Object.keys(actions).forEach((key) => {
      const actionFn = actions[key];
      if (typeof actionFn === 'function') {
        // Direct function - always enabled
        actionProps[key] = () => actionFn(row);
      } else if (typeof actionFn === 'object') {
        // Object with condition and handler
        const shouldShow = actionFn.condition ? actionFn.condition(row) : true;
        actionProps[key] = shouldShow ? () => actionFn.handler(row) : null;

        // Pass tooltip if available
        if (actionFn.tooltip && shouldShow) {
          const tooltipKey = `${key}Tooltip`;
          actionProps[tooltipKey] = actionFn.tooltip;
        }
      }
    });

    return <TableActions {...actionProps} />;
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer>
        <Table size={size}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align || (column.type === 'currency' ? 'right' : 'left')} width={column.width}>
                  {column.label}
                </TableCell>
              ))}
              {actions && (
                <TableCell align="center" width="120">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (actions ? 1 : 0)} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      py: 3
                    }}
                  >
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => (
                <TableRow
                  key={row[rowKey]}
                  hover={!!onRowClick}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align || (column.type === 'currency' ? 'right' : 'left')}>
                      {renderCell(column, row)}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {getRowActions(row)}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationEnabled && paginatedData.length > 0 && (
        <TablePagination
          component="div"
          count={totalRows}
          page={currentPage}
          onPageChange={handlePageChange}
          rowsPerPage={currentRowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={pagination?.rowsPerPageOptions || [5, 10, 15, 25, 50]}
        />
      )}
    </Box>
  );
};

DataTable.propTypes = {
  // Column configuration
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      accessor: PropTypes.string,
      type: PropTypes.oneOf(['text', 'currency', 'date', 'status', 'badge', 'link']),
      render: PropTypes.func,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      primary: PropTypes.bool,
      secondary: PropTypes.bool,
      currencyField: PropTypes.string,
      statusConfig: PropTypes.object,
      onClick: PropTypes.func
    })
  ).isRequired,

  // Data
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  rowKey: PropTypes.string,

  // Pagination
  pagination: PropTypes.shape({
    enabled: PropTypes.bool,
    serverSide: PropTypes.bool,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    total: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number)
  }),

  // Actions
  actions: PropTypes.object,

  // Interactions
  onRowClick: PropTypes.func,

  // Styling
  size: PropTypes.oneOf(['small', 'medium'])
};

export default DataTable;
